import { Box, Grid, IconButton, Tooltip, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { ReactComponent as CheckIcon } from "Components/_Shared/Icons/Assets/check.svg";
import { ReactComponent as ProgressIcon } from "Components/_Shared/Icons/Assets/progress.svg";
import { ReactComponent as UnavailableIcon } from "Components/_Shared/Icons/Assets/unavailable.svg";
import { getListAnalysesOverviewQueryKey, useCloneAnalysis, useSetAnalysisState } from "Infrastructure/ApiZfp/AnalyzerApi";
import { AnalysisDetailState } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { toast } from "sonner";

type Props = {
  readonly analysisId: string;
  readonly versionId: string;
  readonly state: AnalysisDetailState;
};

export const AnalysesTableActionsCell: React.FC<Props> = ({ analysisId, versionId, state }) => {
  const { t } = useResource();
  const theme = useTheme();
  const queryClient = useQueryClient();

  const { mutateAsync: createAnalysisVersion, isPending: isCloning } = useCloneAnalysis({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListAnalysesOverviewQueryKey() });
        toast.success(t(Resources.AnalysesListing.Toast.NewVersion));
      },
    },
  });
  const { mutateAsync: setAnalysisState, isPending: isSettingState } = useSetAnalysisState({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListAnalysesOverviewQueryKey() });
        toast.success(t(Resources.AnalysesListing.Toast.NewState));
      },
    },
  });

  const isMutating = isCloning || isSettingState;

  const handleStateChange = async (newState: AnalysisDetailState) => {
    if (isMutating) return;

    if (newState === AnalysisDetailState.in_progress) {
      await createAnalysisVersion({
        analysisId,
        versionId,
      });

      return;
    }

    await setAnalysisState({
      analysisId,
      versionId,
      data: {
        state: newState,
      },
    });
  };

  return (
    <Box>
      <Grid container spacing={Spacing.M}>
        {state === AnalysisDetailState.in_progress && (
          <>
            <Tooltip title={t(Resources.AnalysesListing.Status.Completed)} placement="top" arrow>
              <IconButton disabled={isMutating} onClick={() => handleStateChange(AnalysisDetailState.completed)}>
                <CheckIcon color={theme.colors.textSecondary} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t(Resources.AnalysesListing.Status.Declined)} placement="top" arrow>
              <IconButton disabled={isMutating} onClick={() => handleStateChange(AnalysisDetailState.declined)}>
                <UnavailableIcon color={theme.colors.textSecondary} />
              </IconButton>
            </Tooltip>
          </>
        )}
        {([AnalysisDetailState.completed, AnalysisDetailState.declined] as AnalysisDetailState[]).includes(state) && (
          <Tooltip title={t(Resources.AnalysesListing.Status.InProgress)} placement="top" arrow>
            <IconButton disabled={isMutating} onClick={() => handleStateChange(AnalysisDetailState.in_progress)}>
              <ProgressIcon color={theme.colors.textSecondary} />
            </IconButton>
          </Tooltip>
        )}
      </Grid>
    </Box>
  );
};
